import React, { useState, useEffect, ReactNode, useRef } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { Gym } from '../models/gym/Gym';
import { ClimbingRoute } from '../models/climbing/ClimbingRoute';
import { getGym, getAllGyms } from '../services/firebase/gym';
import { getClimbingRoutes } from '../services/firebase/climbingRoute';
import { updateClimberFields } from '../services/firebase/climber';
import { getAllClimbs, calculateRankings, getGymRanking } from '../services/firebase/climbs';
import { AvailableColors } from '../models/climbing/RouteColors';
import { GradeSystem, getGradeAsString, ClimbPerformance } from '../models/climbing/Grade';
import { ClimbRecord } from '../models/user/Climber';
import { RouteFeedback } from '../models/climbing/RouteFeedback';
import { RouteFeedbackService } from '../services/firebase/routeFeedbackService';
import { useAuth } from '../contexts/AuthContext';
import { useBackButton } from '../hooks/useBackButton';
import { usePWAInstall } from '../hooks/usePWAInstall';
import ExitMessageOverlay from '../components/ExitMessageOverlay';
import JoinGymDialog from '../components/JoinGymDialog';
import PWAInstallDialog from '../components/modals/PWAInstallDialog';
import PageHeader from '../components/layout/PageHeader';
import HamburgerMenu from '../components/HamburgerMenu';
import GymSelector from '../components/GymSelector';
import BananaCupRankings from '../components/BananaCupRankings';
import Newsletter from '../components/Newsletter';
import MyIconButton from '../components/ui/MyIconButton';

// Import the NEW and refactored CSS files
import '../styles/GymDetail.css';
import '../styles/RankingsList.css';
import '../styles/GymSelector.css';
import BananaCupInfoModal from '../components/modals/BananaCupInfoModal';

const GymDetail: React.FC = () => {
    const { gymId } = useParams<{ gymId: string }>();
    const [searchParams, setSearchParams] = useSearchParams();
    const { showExitMessage, currentMessage } = useBackButton();
    
    const [gym, setGym] = useState<Gym | null>(null);
    const [routes, setRoutes] = useState<ClimbingRoute[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [showJoinDialog, setShowJoinDialog] = useState(false);
    const [isUserMember, setIsUserMember] = useState(false);
    const [userRole, setUserRole] = useState<string | null>(null);
    const [userLoading, setUserLoading] = useState(true);
    const [userClimbedRouteIds, setUserClimbedRouteIds] = useState<string[]>([]);
    const [userClimbs, setUserClimbs] = useState<ClimbRecord[]>([]);
    const [allUserClimbs, setAllUserClimbs] = useState<ClimbRecord[]>([]); // Complete climbs array across all gyms
    const [activeFilters, setActiveFilters] = useState<{ grade?: string, rope?: string, color?: string }>({});
    const [showFilters, setShowFilters] = useState(false);
    const [activeTab, setActiveTab] = useState<'routes' | 'bananaCup' | 'climbs' | 'feedback'>(() => {
        const tabParam = searchParams.get('tab');
        if (tabParam === 'climbs' || tabParam === 'bananaCup' || tabParam === 'feedback') {
            return tabParam;
        }
        return 'routes';
    });
    const [showBananaCupInfo, setShowBananaCupInfo] = useState<boolean>(false);
    const [showNewsletter, setShowNewsletter] = useState<boolean>(false);
    const [userSubscriptionStatus, setUserSubscriptionStatus] = useState<string | undefined>(undefined);
    const [sortBy, setSortBy] = useState<string>('date');
    const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
    const [climbsActiveFilters, setClimbsActiveFilters] = useState<{ grade?: string, performance?: string }>({});
    const [showClimbsFilters, setShowClimbsFilters] = useState(false);
    
    // Route banana calculations
    const [routeBananas, setRouteBananas] = useState<Map<string, number>>(new Map());
    
    // Feedback state
    const [gymFeedbacks, setGymFeedbacks] = useState<RouteFeedback[]>([]);
    const [feedbackLoading, setFeedbackLoading] = useState(false);
    const [feedbackError, setFeedbackError] = useState<string>('');
    const [selectedFeedback, setSelectedFeedback] = useState<RouteFeedback | null>(null);
    const [isDeletingFeedback, setIsDeletingFeedback] = useState(false);
    
    // PWA Installation state
    const { shouldShowInstallPrompt, installPWA, dismissInstallPrompt } = usePWAInstall();
    const [showPWADialog, setShowPWADialog] = useState(false);
    
    const { currentUser, climberProfile, setLastVisitedGym, refreshClimberProfile } = useAuth();
    const navigate = useNavigate();

    // Tab swipe functionality
    const [touchStart, setTouchStart] = useState<number | null>(null);
    const [touchEnd, setTouchEnd] = useState<number | null>(null);
    const [isSwipeActive, setIsSwipeActive] = useState(false);
    const minSwipeDistance = 50;
    
    // Ref for tab container to enable scrolling
    const tabsContainerRef = useRef<HTMLDivElement>(null);

    // Define tab order for navigation
    const tabOrder: ('routes' | 'bananaCup' | 'climbs' | 'feedback')[] = gym?.feedbackSettings?.enabled 
        ? ['routes', 'climbs', 'bananaCup', 'feedback']
        : ['routes', 'climbs', 'bananaCup'];

    const handleTabSwipe = (direction: 'left' | 'right') => {
        const currentIndex = tabOrder.indexOf(activeTab);
        let newIndex: number;
        
        if (direction === 'left' && currentIndex < tabOrder.length - 1) {
            newIndex = currentIndex + 1;
        } else if (direction === 'right' && currentIndex > 0) {
            newIndex = currentIndex - 1;
        } else {
            return; // No change needed
        }
        
        handleTabChange(tabOrder[newIndex]);
    };

    const handleTouchStart = (e: React.TouchEvent) => {
        setTouchEnd(null);
        setTouchStart(e.targetTouches[0].clientX);
        setIsSwipeActive(true);
    };

    const handleTouchMove = (e: React.TouchEvent) => {
        setTouchEnd(e.targetTouches[0].clientX);
    };

    const handleTouchEnd = () => {
        setIsSwipeActive(false);
        
        if (!touchStart || !touchEnd) return;
        
        const distance = touchStart - touchEnd;
        const isLeftSwipe = distance > minSwipeDistance;
        const isRightSwipe = distance < -minSwipeDistance;

        if (isLeftSwipe) {
            handleTabSwipe('left');
        } else if (isRightSwipe) {
            handleTabSwipe('right');
        }
    };

    // Wheel event for horizontal scrolling on desktop
    const handleWheel = (e: React.WheelEvent) => {
        // Only handle horizontal scroll or shift+scroll
        if (Math.abs(e.deltaX) > Math.abs(e.deltaY) || e.shiftKey) {
            e.preventDefault();
            
            if (e.deltaX > 0 || (e.shiftKey && e.deltaY > 0)) {
                handleTabSwipe('left');
            } else if (e.deltaX < 0 || (e.shiftKey && e.deltaY < 0)) {
                handleTabSwipe('right');
            }
        }
    };

    // --- Fetching useEffects (Keep Original) ---
    useEffect(() => {
        const fetchGymAndRoutes = async () => {
            if (!gymId) { setError('Invalid gym ID'); setLoading(false); return; }
            try {
                setLoading(true);
                setError(''); // Clear any previous errors
                // Reset state when switching gyms
                setRoutes([]);
                setUserClimbs([]);
                setAllUserClimbs([]);
                setUserClimbedRouteIds([]);
                setActiveFilters({});
                setClimbsActiveFilters({});
                setShowFilters(false);
                setShowClimbsFilters(false);
                // Don't reset the activeTab here since it should be controlled by URL params
                
                const gymData = await getGym(gymId);
                if (!gymData) { setError('Gym not found'); setLoading(false); return; }
                setGym(gymData);
                const routesData = await getClimbingRoutes(gymId);
                setRoutes(routesData);
                setError('');
            } catch (err: any) { console.error('Error fetching gym data:', err); setError('Failed to load gym data.'); }
            finally { setLoading(false); }
        };
        fetchGymAndRoutes();
    }, [gymId]);

    useEffect(() => {
        const checkGymMembership = () => {
            if (!currentUser || !gymId || !climberProfile) { 
                setUserLoading(false); 
                return; 
            }
            
            try {
                setUserLoading(true);
                // Reset membership state when switching gyms
                setIsUserMember(false);
                setUserRole(null);
                setUserSubscriptionStatus(undefined);
                
                // Use climber profile from context instead of fetching again
                const isMember = climberProfile.isMemberOfGym(gymId);
                setIsUserMember(isMember);
                setUserRole(isMember ? climberProfile.getRoleInGym(gymId) : null);
                
                // Get the subscription status
                setUserSubscriptionStatus(climberProfile.subscribed);
                
                // Set this as the last visited gym if user is a member
                if (isMember) {
                    setLastVisitedGym(gymId);
                }
            } catch (err) { 
                console.error('Error checking gym membership:', err); 
                setIsUserMember(false); 
                setUserRole(null);
                setUserSubscriptionStatus(undefined);
            }
            finally { setUserLoading(false); }
        };
        checkGymMembership();
    }, [currentUser, gymId, climberProfile, setLastVisitedGym]);

    useEffect(() => {
        const fetchUserClimbs = async () => {
            if (!currentUser || !gymId) {
                setUserClimbs([]);
                setAllUserClimbs([]);
                setUserClimbedRouteIds([]);
                return;
            }
            try {
                const climbs = await getAllClimbs(currentUser.uid);
                setAllUserClimbs(climbs); // Store complete climbs array
                const gymClimbs = climbs.filter(climb => climb.gymId === gymId);
                setUserClimbs(gymClimbs);
                setUserClimbedRouteIds(gymClimbs.map(climb => climb.routeId));
            } catch (err) { 
                console.error('Error fetching user climbs:', err); 
                setUserClimbs([]);
                setAllUserClimbs([]);
                setUserClimbedRouteIds([]);
            }
        };
        fetchUserClimbs();
    }, [currentUser, gymId]);

    // Fetch gym feedbacks
    useEffect(() => {
        const fetchGymFeedbacks = async () => {
            if (!gymId || !gym?.feedbackSettings?.enabled) {
                setGymFeedbacks([]);
                setFeedbackError('');
                return;
            }
            
            try {
                setFeedbackLoading(true);
                setFeedbackError('');
                const feedbacks = await RouteFeedbackService.getGymFeedbacks(gymId);
                setGymFeedbacks(feedbacks);
            } catch (err) {
                console.error('Error fetching gym feedbacks:', err);
                setFeedbackError(err instanceof Error ? err.message : 'Failed to load feedback');
                setGymFeedbacks([]);
            } finally {
                setFeedbackLoading(false);
            }
        };
        
        fetchGymFeedbacks();
    }, [gymId, gym?.feedbackSettings?.enabled]);

    // PWA Installation Dialog
    useEffect(() => {

        // Show PWA install dialog after gym data loads and user is a member
        if (gym && isUserMember && !userLoading && shouldShowInstallPrompt()) {
            // Add a small delay to let the page settle
            const timer = setTimeout(() => {
                setShowPWADialog(true);
            }, 2000);
            
            return () => clearTimeout(timer);
        }
    }, [gym, isUserMember, userLoading, shouldShowInstallPrompt]);

    useEffect(() => {
        // Reset join dialog when switching gyms
        setShowJoinDialog(false);
        
        if (gym && !isUserMember && !userLoading && !loading) {
            setShowJoinDialog(true);
        }
    }, [gym, isUserMember, userLoading, loading]);

    // Check if Newsletter should be shown when user goes to the Banana Cup tab
    useEffect(() => {
        if (activeTab === 'bananaCup' && userSubscriptionStatus === 'o' && currentUser) {
            setShowNewsletter(true);
        } else {
            setShowNewsletter(false);
        }
    }, [activeTab, userSubscriptionStatus, currentUser]);

    // Scroll active tab into view when tab changes or component loads
    useEffect(() => {
        if (!loading) {
            // Small delay to ensure DOM is ready
            setTimeout(() => scrollActiveTabIntoView(activeTab), 100);
        }
    }, [activeTab, loading]);

    // Calculate bananas for all routes when routes change
    useEffect(() => {
        const calculateAllRouteBananas = async () => {
            if (!gymId || routes.length === 0) return;
            
            const bananasMap = new Map<string, number>();
            
            for (const route of routes) {
                const bananas = await calculateRouteBananas(route.id);
                bananasMap.set(route.id, bananas);
            }
            
            setRouteBananas(bananasMap);
        };
        
        calculateAllRouteBananas();
    }, [routes, gymId]);

    // --- Handler Functions (Restore Original Logic) ---
    const handleJoinGym = async () => {
        if (!currentUser || !gymId || !climberProfile) { setShowJoinDialog(false); return; }
        try {
            // Use climber profile from context
            const climber = climberProfile;
            climber.updateGymMembership(gymId, 'climber');
            await updateClimberFields(currentUser.uid, { gymMemberships: climber.gymMemberships });
            
            // Refresh the climber profile in context
            await refreshClimberProfile();
            
            setIsUserMember(true);
            setUserRole('climber');
            setShowJoinDialog(false);
        } catch (err) { console.error('Error joining gym:', err); }
    };

    const handleLeaveGym = async () => { // Keep this, though trigger isn't shown
        if (!currentUser || !gymId || !climberProfile) return;
        try {
            // Use climber profile from context
            const climber = climberProfile;
            climber.removeGymMembership(gymId);
            await updateClimberFields(currentUser.uid, { gymMemberships: climber.gymMemberships });
            
            // Refresh the climber profile in context
            await refreshClimberProfile();
            
            navigate('/gyms');
        } catch (err) { console.error('Error leaving gym:', err); }
    };

    // Handle Newsletter component interactions
    const handleNewsletterSkip = async () => {
        setShowNewsletter(false);
        setUserSubscriptionStatus('N');
    };

    const handleJoinGymCancel = () => {
        // Use React's callback to ensure we're working with the latest state
        setShowJoinDialog(prev => {
            if (prev) {
                // Only navigate if the dialog was actually showing
                navigate('/gyms');
            }
            return false;
        });
    };

    const handleAddRoute = () => {
        if (gymId) navigate(`/gyms/${gymId}/routes/add`);
    };

    const handleEditGym = () => { // Keep this, though trigger isn't shown
        if (gymId) navigate(`/gyms/${gymId}/edit`);
    };

    const handleRouteClick = (routeId: string) => {
        if (gymId) navigate(`/gyms/${gymId}/routes/${routeId}`);
    };

    const handleClimbClick = (climbIndex: number) => {
        if (gymId) navigate(`/gyms/${gymId}/climbs/${climbIndex}`);
    };

    // Helper to find the original index of a climb in the user's complete climbs array
    // This finds the index in the user's FULL climbs array (all gyms), 
    // which is what ClimbDetail expects
    const findOriginalClimbIndex = (targetClimb: ClimbRecord): number => {
        return allUserClimbs.findIndex(climb => 
            climb.routeId === targetClimb.routeId && 
            climb.date === targetClimb.date &&
            climb.performance === targetClimb.performance
        );
    };

    // Function to scroll active tab into view
    const scrollActiveTabIntoView = (tab: string) => {
        if (!tabsContainerRef.current) return;
        
        const activeTabButton = tabsContainerRef.current.querySelector(`.gym-tab.active`) as HTMLElement;
        if (!activeTabButton) return;
        
        const container = tabsContainerRef.current;
        const containerRect = container.getBoundingClientRect();
        const tabRect = activeTabButton.getBoundingClientRect();
        
        // Calculate if tab is out of view
        const isTabOutOfView = tabRect.left < containerRect.left || tabRect.right > containerRect.right;
        
        if (isTabOutOfView) {
            // Calculate scroll position to center the tab
            const containerWidth = container.clientWidth;
            const tabWidth = activeTabButton.offsetWidth;
            const tabOffsetLeft = activeTabButton.offsetLeft;
            
            const scrollLeft = tabOffsetLeft - (containerWidth / 2) + (tabWidth / 2);
            
            container.scrollTo({
                left: Math.max(0, scrollLeft),
                behavior: 'smooth'
            });
        }
    };

    const handleTabChange = (tab: 'routes' | 'bananaCup' | 'climbs' | 'feedback') => {
        setActiveTab(tab);
        const newSearchParams = new URLSearchParams(searchParams);
        if (tab === 'routes') {
            newSearchParams.delete('tab'); // Default tab, no need for URL param
        } else {
            newSearchParams.set('tab', tab);
        }
        setSearchParams(newSearchParams, { replace: true });
        
        // Scroll the active tab into view after a short delay to ensure DOM update
        setTimeout(() => scrollActiveTabIntoView(tab), 50);
    };

    const handleGymChange = (newGymId: string) => {
        if (newGymId !== gymId) {
            navigate(`/gyms/${newGymId}`);
        }
    };

    // PWA Installation handlers
    const handlePWAInstall = async () => {
        const success = await installPWA();
        if (success) {
            setShowPWADialog(false);
            sessionStorage.setItem('pwa-install-shown', 'true');
        }
        return success;
    };

    const handlePWADismiss = () => {
        setShowPWADialog(false);
        dismissInstallPrompt();
    };

    const handleAddGym = () => {
        navigate('/gyms/add');
    };

    // --- Helper Functions for Climbs Tab ---
    const getRouteForClimb = (routeId: string, climbData?: ClimbRecord): ClimbingRoute | null => {
        // First try to find the route in active routes
        const activeRoute = routes.find(route => route.id === routeId);
        if (activeRoute) {
            return activeRoute;
        }
        
        // If route not found and climb data is provided, create route from embedded data
        if (climbData) {
            console.warn(`Route ${routeId} not found in active routes, using embedded climb data`);
            return new ClimbingRoute(routeId, {
                grade: climbData.grade,
                color: climbData.color || '',
                rope: climbData.rope || '',
                setterName: climbData.setterName || 'Unknown setter',
                setterId: climbData.setterId || '',
                status: climbData.status || 'archived',
                date: climbData.routeDate || climbData.date,
                location: climbData.location,
                points: climbData.points || 1,
                note: climbData.routeNote,
                comments: climbData.routeComments,
                picture: climbData.picture
            });
        }
        
        return null;
    };

    const getClimbsFilterOptions = () => {
        const grades = new Set<string>();
        const performances = new Set<string>();
        
        userClimbs.forEach(climb => {
            const route = getRouteForClimb(climb.routeId, climb);
            if (route && route.grade !== undefined) {
                grades.add(getGradeAsString(route.grade, GradeSystem.FRENCH));
            }
            if (climb.performance) {
                performances.add(getPerformanceLabel(climb.performance, climb.hangs));
            }
        });
        
        return {
            grades: Array.from(grades).sort(),
            performances: Array.from(performances).sort()
        };
    };

    const toggleClimbsFilters = () => {
        setShowClimbsFilters(!showClimbsFilters);
    };

    const applyClimbsFilter = (filterType: 'grade' | 'performance', value: string) => {
        setClimbsActiveFilters(prev => {
            const newFilters = { ...prev };
            if (prev[filterType] === value) {
                delete newFilters[filterType];
            } else {
                newFilters[filterType] = value;
            }
            return newFilters;
        });
    };

    const clearClimbsFilters = () => {
        setClimbsActiveFilters({});
    };

    // Helper function to get alternating background colors based on date
    const getClimbBackgroundColor = (climbs: ClimbRecord[], currentIndex: number): string => {
        if (currentIndex === 0) return '#ffffff'; // First item is always white
        
        const currentClimb = climbs[currentIndex];
        const previousClimb = climbs[currentIndex - 1];
        
        // Get date strings for comparison (YYYY-MM-DD format)
        const currentDate = new Date(currentClimb.date).toDateString();
        const previousDate = new Date(previousClimb.date).toDateString();
        
        // If the date is different from the previous climb, alternate the color
        if (currentDate !== previousDate) {
            const previousColor = getClimbBackgroundColor(climbs, currentIndex - 1);
            return previousColor === '#ffffff' ? '#f0fdf4' : '#ffffff'; // Switch between white and very light green
        }
        
        // Same date as previous climb, use same color
        return getClimbBackgroundColor(climbs, currentIndex - 1);
    };

    // --- Helper Functions (Restore Original Logic) ---
    const isColorDark = (hexColor: string): boolean => {
        if (!hexColor || hexColor.length < 7) return false; // Handle invalid hex
        const r = parseInt(hexColor.slice(1, 3), 16);
        const g = parseInt(hexColor.slice(3, 5), 16);
        const b = parseInt(hexColor.slice(5, 7), 16);
        const brightness = (r * 299 + g * 587 + b * 114) / 1000;
        return brightness < 128;
    };

    const getColorHex = (colorName?: string): string => {
        if (!colorName) return '#cccccc'; // Default gray
        const color = AvailableColors.find(c => c.value === colorName);
        return color ? color.hexCode : '#cccccc';
    };

    const getUserClimbInfo = (routeId: string): { hasClimbed: boolean, performance?: ClimbPerformance, hangs?: number } => {
        if (!userClimbedRouteIds.includes(routeId)) { return { hasClimbed: false }; }
        const routeClimbs = userClimbs.filter(climb => climb.routeId === routeId);
        if (routeClimbs.length === 0) { return { hasClimbed: true, performance: ClimbPerformance.TOPPED }; } // Should not happen if ID is in list, but safe default
        const bestClimbs = routeClimbs.filter(climb => [ClimbPerformance.ON_SIGHT, ClimbPerformance.REDPOINT, ClimbPerformance.FLASH].includes(climb.performance));
        if (bestClimbs.length > 0) {
            if (bestClimbs.some(c => c.performance === ClimbPerformance.ON_SIGHT)) return { hasClimbed: true, performance: ClimbPerformance.ON_SIGHT };
            if (bestClimbs.some(c => c.performance === ClimbPerformance.FLASH)) return { hasClimbed: true, performance: ClimbPerformance.FLASH };
            return { hasClimbed: true, performance: ClimbPerformance.REDPOINT };
        }
        const hangsClimbs = routeClimbs.filter(climb => [ClimbPerformance.NOT_TOPPED, ClimbPerformance.BLOCKS].includes(climb.performance) && climb.hangs !== undefined);
        if (hangsClimbs.length > 0) {
            const bestHangClimb = hangsClimbs.reduce((best, current) => (best.hangs! < current.hangs!) ? best : current);
            return { hasClimbed: true, performance: bestHangClimb.performance, hangs: bestHangClimb.hangs };
        }
        // Fallback if only TOPPED or other uncategorized climbs exist
        return { hasClimbed: true, performance: routeClimbs[0].performance };
    };

    // Calculate bananas for a specific route (what a new climber would earn)
    const calculateRouteBananas = async (routeId: string): Promise<number> => {
        try {
            if (!gymId) return 0;
            
            // Get all rankings for this gym
            const rankingEntries = await getGymRanking(gymId);
            
            // Count how many climbers completed this specific route
            const climbersOnRoute = rankingEntries.filter(entry => entry.routeId === routeId).length;
            
            // Find the route to get its base points
            const route = routes.find(r => r.id === routeId);
            const baseBananas = route?.points || 100;
            
            if (climbersOnRoute === 0) {
                // No one has completed this route yet, so a new climber would get full points
                return baseBananas;
            }
            
            // Calculate what a NEW climber would earn (base bananas divided by total climbers + 1)
            const newClimberCount = climbersOnRoute + 1;
            const bananasPerClimber = Math.round(baseBananas / newClimberCount);
            
            return bananasPerClimber;
        } catch (error) {
            console.error('Error calculating route bananas:', error);
            return 0;
        }
    };

    // Helper function to truncate setter name to 8 characters
    const truncateSetterName = (name: string): string => {
        if (!name) return 'Unknown setter';
        if (name.length <= 8) return name;
        return name.substring(0, 8) + '...';
    };

    const getPerformanceLabel = (performance: ClimbPerformance, hangs?: number): string => {
        switch (performance) {
            case ClimbPerformance.TOPPED: return 'Topped';
            case ClimbPerformance.FLASH: return 'Flash';
            case ClimbPerformance.ON_SIGHT: return 'On Sight';
            case ClimbPerformance.REDPOINT: return 'Topped';
            case ClimbPerformance.NOT_TOPPED: return hangs !== undefined ? `${hangs} ${hangs === 1 ? 'Hang' : 'Hangs'}` : 'Attempted';
            case ClimbPerformance.BLOCKS: return 'Blocks'; // Assuming 'Blocks' means a specific type of attempt
            default: return String(performance);
        }
    };

    const getPerformanceClass = (performance: ClimbPerformance): string => {
        switch (performance) {
            case ClimbPerformance.TOPPED: return 'topped';
            case ClimbPerformance.FLASH: return 'flash';
            case ClimbPerformance.ON_SIGHT: return 'onsight';
            case ClimbPerformance.REDPOINT: return 'topped';
            case ClimbPerformance.NOT_TOPPED: return 'not-topped';
            case ClimbPerformance.BLOCKS: return 'blocks'; // Add style for blocks if needed
            default: return '';
        }
    };

    const canCreateRoutes = (): boolean => {
        return userRole === 'setter' || userRole === 'owner' || userRole === 'fre'; // 'fre' role seems specific, ensure it's intended
    };

    const canEditGym = (): boolean => { // Keep this, though trigger isn't shown
        return userRole === 'owner' || userRole === 'fre';
    };

    const canAddGyms = (): boolean => {
        return userRole === 'fre';
    };

    // --- Filter/Sort Logic (Restore Original Logic) ---
    const getUniqueFilterOptions = () => {
        const grades = new Set<string>();
        const ropes = new Set<string>();
        const colors = new Set<string>();
        routes.forEach(route => {
            if (route.grade !== undefined) grades.add(getGradeAsString(route.grade, GradeSystem.FRENCH));
            if (route.rope) ropes.add(route.rope);
            if (route.color) colors.add(route.color);
        });
        return {
            grades: Array.from(grades).sort(), // Consider custom grade sorting if needed
            ropes: Array.from(ropes).sort(),
            colors: Array.from(colors).sort()
        };
    };

    const filterOptions = getUniqueFilterOptions(); // Calculate once

    const filteredRoutes = routes.filter(route => {
        if (Object.keys(activeFilters).length === 0) return true;
        if (activeFilters.grade && (route.grade === undefined || getGradeAsString(route.grade, GradeSystem.FRENCH) !== activeFilters.grade)) return false;
        if (activeFilters.rope && route.rope !== activeFilters.rope) return false;
        if (activeFilters.color && route.color !== activeFilters.color) return false;
        return true;
    });

    const toggleFilters = () => { setShowFilters(!showFilters); };

    const toggleBananaCupInfo = () => { setShowBananaCupInfo(!showBananaCupInfo); };

    const applyFilter = (filterType: 'grade' | 'rope' | 'color', value: string) => {
        setActiveFilters(prev => {
            const newFilters = { ...prev };
            if (prev[filterType] === value) delete newFilters[filterType];
            else newFilters[filterType] = value;
            return newFilters;
        });
    };

    const clearFilters = () => { setActiveFilters({}); };

    // --- Filter/Sort Logic for Climbs ---
    const climbsFilterOptions = getClimbsFilterOptions();

    const filteredClimbs = userClimbs.filter(climb => {
        if (Object.keys(climbsActiveFilters).length === 0) return true;
        
        const route = getRouteForClimb(climb.routeId, climb);
        
        if (climbsActiveFilters.grade) {
            if (!route || route.grade === undefined || getGradeAsString(route.grade, GradeSystem.FRENCH) !== climbsActiveFilters.grade) {
                return false;
            }
        }
        
        if (climbsActiveFilters.performance) {
            const performanceLabel = getPerformanceLabel(climb.performance, climb.hangs);
            if (performanceLabel !== climbsActiveFilters.performance) {
                return false;
            }
        }
        
        return true;
    });

    const sortedClimbs = [...filteredClimbs].sort((a, b) => {
        if (sortBy === 'date') {
            const dateA = a.date || 0;
            const dateB = b.date || 0;
            return sortDirection === 'asc' ? dateA - dateB : dateB - dateA;
        } else if (sortBy === 'grade') {
            const routeA = getRouteForClimb(a.routeId, a);
            const routeB = getRouteForClimb(b.routeId, b);
            const gradeA = routeA?.grade ?? (sortDirection === 'asc' ? Infinity : -Infinity);
            const gradeB = routeB?.grade ?? (sortDirection === 'asc' ? Infinity : -Infinity);
            return sortDirection === 'asc' ? gradeA - gradeB : gradeB - gradeA;
        } else if (sortBy === 'performance') {
            const getPriorityValue = (climb: ClimbRecord): number => {
                if ([ClimbPerformance.ON_SIGHT, ClimbPerformance.REDPOINT, ClimbPerformance.FLASH, ClimbPerformance.TOPPED].includes(climb.performance)) {
                    return 0;
                }
                if ([ClimbPerformance.NOT_TOPPED, ClimbPerformance.BLOCKS].includes(climb.performance) && climb.hangs !== undefined) {
                    return climb.hangs;
                }
                return 997;
            };
            
            const priorityA = getPriorityValue(a);
            const priorityB = getPriorityValue(b);
            return sortDirection === 'desc' ? priorityA - priorityB : priorityB - priorityA;
        }
        return 0;
    });

    const handleSortChange = (newSortBy: string) => {
        if (sortBy === newSortBy) {
            setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
        } else {
            setSortBy(newSortBy);
            // Default sorting direction: desc for date and performance, asc for others
            setSortDirection(['date', 'performance'].includes(newSortBy) ? 'desc' : 'asc');
        }
    };

    const sortedRoutes = [...filteredRoutes].sort((a, b) => {
        let compareValueA: any;
        let compareValueB: any;

        if (sortBy === 'grade') {
            const gradeA = a.grade ?? (sortDirection === 'asc' ? Infinity : -Infinity);
            const gradeB = b.grade ?? (sortDirection === 'asc' ? Infinity : -Infinity);
            return sortDirection === 'asc' ? gradeA - gradeB : gradeB - gradeA;
            
        } else if (sortBy === 'date') {
            // Ensure dates are valid or provide defaults for sorting
            const dateA = a.date ? new Date(a.date).getTime() : (sortDirection === 'asc' ? 0 : Infinity);
            const dateB = b.date ? new Date(b.date).getTime() : (sortDirection === 'asc' ? 0 : Infinity);
            // Handle potential NaN from invalid dates
            if (isNaN(dateA) && isNaN(dateB)) return 0;
            if (isNaN(dateA)) return sortDirection === 'asc' ? -1 : 1; // Put invalid dates first/last consistently
            if (isNaN(dateB)) return sortDirection === 'asc' ? 1 : -1;
            return sortDirection === 'asc' ? dateA - dateB : dateB - dateA;
            
        } else if (sortBy === 'rope') {
            // Sort by rope number as integers, not strings
            const ropeA = parseInt(a.rope || '0', 10);
            const ropeB = parseInt(b.rope || '0', 10);
            // Handle NaN values
            if (isNaN(ropeA) && isNaN(ropeB)) return 0;
            if (isNaN(ropeA)) return sortDirection === 'asc' ? -1 : 1;
            if (isNaN(ropeB)) return sortDirection === 'asc' ? 1 : -1;
            return sortDirection === 'asc' ? ropeA - ropeB : ropeB - ropeA;
            
        } else if (sortBy === 'setter') {
            // Sort by setter name alphabetically
            const setterA = a.setterName || '';
            const setterB = b.setterName || '';
            const comparison = setterA.localeCompare(setterB);
            return sortDirection === 'asc' ? comparison : -comparison;
            
        } else if (sortBy === 'performance') {
            // Sort by performance with priority: topped, 1 hang, 2 hangs, ..., '-'
            const climbInfoA = getUserClimbInfo(a.id);
            const climbInfoB = getUserClimbInfo(b.id);
            
            // Helper function to get numeric priority (lower is better)
            const getPriorityValue = (info: { hasClimbed: boolean, performance?: ClimbPerformance, hangs?: number }): number => {
                if (!info.hasClimbed) return 999; // Not climbed goes last
                if (!info.performance) return 998; // No performance data
                
                // Topped/flash/onsight/redpoint get priority
                if ([ClimbPerformance.ON_SIGHT, ClimbPerformance.REDPOINT, ClimbPerformance.FLASH, ClimbPerformance.TOPPED].includes(info.performance)) {
                    return 0;
                }
                
                // Then sort by number of hangs (1 hang, 2 hangs, etc.)
                if ([ClimbPerformance.NOT_TOPPED, ClimbPerformance.BLOCKS].includes(info.performance) && info.hangs !== undefined) {
                    return info.hangs;
                }
                
                return 997; // Other performance types
            };
            
            const priorityA = getPriorityValue(climbInfoA);
            const priorityB = getPriorityValue(climbInfoB);
            
            // Sorting from best to worst (smallest number to largest)
            return sortDirection === 'desc' ? priorityA - priorityB : priorityB - priorityA;
            
        } else if (sortBy === 'bananas') {
            // Sort by banana count
            const bananasA = routeBananas.get(a.id) || 0;
            const bananasB = routeBananas.get(b.id) || 0;
            return sortDirection === 'asc' ? bananasA - bananasB : bananasB - bananasA;
            
        } else {
            // Add a default case or handle other potential sort keys
            return 0; // Default: no change in order
        }
    });


    // Configure header actions based on the active tab
    const headerActions: ReactNode[] = [];
    // All buttons are now floating action buttons

    const handleDeleteFeedback = async () => {
        if (!selectedFeedback || !gymId) return;
        
        setIsDeletingFeedback(true);
        try {
            await RouteFeedbackService.deleteFeedback(gymId, selectedFeedback.id);
            setGymFeedbacks(prev => prev.filter(f => f.id !== selectedFeedback.id));
            setSelectedFeedback(null);
        } catch (err) {
            console.error('Error deleting feedback:', err);
            // Optionally, add an error toast message here
        } finally {
            setIsDeletingFeedback(false);
        }
    };

    // --- Render Logic ---
    // Only show full loading screen on initial page load or if no gym data at all
    if ((loading && !gym) || (userLoading && !currentUser)) {
        return (
            <div className="gym-detail-container">
                <PageHeader title="Gym Details" />
                <div className="loading-indicator">Loading gym data...</div>
            </div>
        );
    }

    // Check for gym existence *after* initial loading is complete
    if (error || !gym) {
        return (
            <div className="gym-detail-container">
                <PageHeader title="Gym Details" />
                {/* If gym is null but no specific error, show generic message */}
                <div className="error-message">{error || 'Gym not found or could not be loaded.'}</div>
            </div>
        );
    }

    // Now we know gym is not null here
    // Detect mobile
    const isMobile = typeof window !== 'undefined' ? window.innerWidth < 1024 : false;

    return (
        <div 
            className={`gym-detail-container ${isSwipeActive ? 'swipe-active' : ''}`}
            style={{ minHeight: isMobile ? 'calc(var(--vh, 1vh) * 100)' : 'auto' }}
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
            onWheel={handleWheel}
        >
            {/* **FIXED**: Restore component call and add null check for gym.name */}
            {showJoinDialog && (
                <JoinGymDialog
                    gymName={gym.name || 'Unnamed Gym'}
                    onConfirm={handleJoinGym}
                    onCancel={handleJoinGymCancel}
                />
            )}

            {/* PWA Installation Dialog */}
            <PWAInstallDialog
                show={showPWADialog}
                onClose={handlePWADismiss}
                onInstall={handlePWAInstall}
            />

            {/* Banana Cup Info Modal */}
            <BananaCupInfoModal
                isOpen={showBananaCupInfo}
                onClose={() => setShowBananaCupInfo(false)}
            />

            {/* **SAFE**: gym is guaranteed to be non-null here */}
            <header className="page-header">
                {/* Left side with gym selector */}
                <div className="title-container">
                    <GymSelector 
                        selectedGym={gym}
                        onGymChange={handleGymChange}
                        onAddGym={handleAddGym}
                        showAddButton={canAddGyms()}
                        disabled={loading}
                    />
                </div>
                
                {/* Right side with actions/hamburger */}
                <div className="header-actions">
                    {headerActions.map((action, index) => (
                        <div key={`header-action-${index}`} className="header-action-item">
                            {action}
                        </div>
                    ))}
                    <div className="header-action-item">
                        <HamburgerMenu />
                    </div>
                </div>
            </header>

            <div className="gym-tabs">
                <div className="gym-tabs-left" ref={tabsContainerRef}>
                    <button
                        className={`gym-tab ${activeTab === 'routes' ? 'active' : ''}`}
                        onClick={() => handleTabChange('routes')}
                    >
                        Routes
                    </button>
                    <button
                        className={`gym-tab ${activeTab === 'climbs' ? 'active' : ''}`}
                        onClick={() => handleTabChange('climbs')}
                    >
                        Climbs
                    </button>
                    <button
                        className={`gym-tab ${activeTab === 'bananaCup' ? 'active' : ''}`}
                        onClick={() => handleTabChange('bananaCup')}
                    >
                        Bananas
                    </button>
                    {gym?.feedbackSettings?.enabled && (
                        <button
                            className={`gym-tab ${activeTab === 'feedback' ? 'active' : ''}`}
                            onClick={() => handleTabChange('feedback')}
                        >
                            Feedback
                        </button>
                    )}
                </div>
            </div>

            {activeTab === 'routes' && (
                <div className="routes-section tab-content-area">
                    {loading ? (
                        <div className="tab-loading-indicator">Loading routes...</div>
                    ) : (
                        <>
                            {/* --- Filter Bottom Drawer --- */}
                            {showFilters && (
                                <div className="filter-drawer-overlay" onClick={() => setShowFilters(false)}>
                                    <div className="filter-drawer" onClick={(e) => e.stopPropagation()}>
                                        <div className="filter-drawer-header">
                                            <h3>Filter Routes</h3>
                                            <button className="filter-drawer-close" onClick={() => setShowFilters(false)}>×</button>
                                        </div>
                                        <div className="filter-drawer-content">
                                            {Object.keys(activeFilters).length > 0 && (
                                                <div className="active-filters">
                                                    <span>Active:</span>
                                                    {activeFilters.grade && <span className="active-filter-tag">Grade: {activeFilters.grade}</span>}
                                                    {activeFilters.rope && <span className="active-filter-tag">Rope: {activeFilters.rope}</span>}
                                                    {activeFilters.color && <span className="active-filter-tag">Color: {activeFilters.color}</span>}
                                                    <button className="clear-filters-button" onClick={clearFilters}>Clear All</button>
                                                </div>
                                            )}

                                            <div className="filters-container">
                                                {filterOptions.grades.length > 0 && (
                                                    <div className="filter-group">
                                                        <h3>Grade</h3>
                                                        <div className="filter-options">
                                                            {filterOptions.grades.map(grade => (
                                                                <button key={`grade-${grade}`} className={`filter-option ${activeFilters.grade === grade ? 'active' : ''}`} onClick={() => applyFilter('grade', grade)}>
                                                                    {grade}
                                                                </button>
                                                            ))}
                                                        </div>
                                                    </div>
                                                )}
                                                {filterOptions.ropes.length > 0 && (
                                                    <div className="filter-group">
                                                        <h3>Rope</h3>
                                                        <div className="filter-options">
                                                            {filterOptions.ropes.map(rope => (
                                                                <button key={`rope-${rope}`} className={`filter-option ${activeFilters.rope === rope ? 'active' : ''}`} onClick={() => applyFilter('rope', rope)}>
                                                                    {rope}
                                                                </button>
                                                            ))}
                                                        </div>
                                                    </div>
                                                )}
                                                {filterOptions.colors.length > 0 && (
                                                    <div className="filter-group">
                                                        <h3>Color</h3>
                                                        <div className="filter-options">
                                                            {filterOptions.colors.map(color => {
                                                                const colorHex = getColorHex(color);
                                                                return (
                                                                    <button key={`color-${color}`} className={`filter-option color-filter ${activeFilters.color === color ? 'active' : ''}`} onClick={() => applyFilter('color', color)} style={{ backgroundColor: colorHex, color: isColorDark(colorHex) ? 'white' : 'black' }}>
                                                                        {color}
                                                                    </button>
                                                                );
                                                            })}
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Active filters indicator */}
                            {Object.keys(activeFilters).length > 0 && (
                                <div className="active-filters-summary">
                                    <span>Filters applied: {Object.keys(activeFilters).length}</span>
                                    <button className="clear-filters-button" onClick={clearFilters}>Clear All</button>
                                </div>
                            )}


                    {/* --- Routes List (JSX structure verified) --- */}
                    {routes.length === 0 ? (
                        <div className="empty-routes"> <p>No climbing routes yet</p> </div>
                    ) : filteredRoutes.length === 0 ? (
                        <div className="empty-routes"> <p>No routes match your filters</p> <button className="clear-filters-button" onClick={clearFilters}>Clear Filters</button> </div>
                    ) : (
                        <div className="rankings-list-container">
                            <div className="rankings-list-scrollable">
                                <div className="rankings-list-header">
                                    <div 
                                        className={`ranking-list-header-grade ${sortBy === 'grade' ? 'sortable active' : 'sortable'}`} 
                                        onClick={() => handleSortChange('grade')}
                                    >
                                        Grade {sortBy === 'grade' && <span className="sort-direction">{sortDirection === 'asc' ? '↑' : '↓'}</span>}
                                    </div>
                                    <div 
                                        className={`ranking-list-header-setter ${sortBy === 'setter' ? 'sortable active' : 'sortable'}`} 
                                        onClick={() => handleSortChange('setter')}
                                    >
                                        Setter {sortBy === 'setter' && <span className="sort-direction">{sortDirection === 'asc' ? '↑' : '↓'}</span>}
                                    </div>
                                    <div 
                                        className={`ranking-list-header-rope ${sortBy === 'rope' ? 'sortable active' : 'sortable'}`} 
                                        onClick={() => handleSortChange('rope')}
                                    >
                                        Rope {sortBy === 'rope' && <span className="sort-direction">{sortDirection === 'asc' ? '↑' : '↓'}</span>}
                                    </div>
                                    <div 
                                        className={`ranking-list-header-bananas ${sortBy === 'bananas' ? 'sortable active' : 'sortable'}`} 
                                        onClick={() => handleSortChange('bananas')}
                                    >
                                        🍌 {sortBy === 'bananas' && <span className="sort-direction">{sortDirection === 'asc' ? '↑' : '↓'}</span>}
                                    </div>
                                    <div 
                                        className={`ranking-list-header-performance ${sortBy === 'performance' ? 'sortable active' : 'sortable'}`} 
                                        onClick={() => handleSortChange('performance')}
                                    >
                                        Performance {sortBy === 'performance' && <span className="sort-direction">{sortDirection === 'asc' ? '↑' : '↓'}</span>}
                                    </div>
                                </div>
                                {sortedRoutes.map(route => {
                                    const climbInfo = getUserClimbInfo(route.id);
                                    const colorHex = getColorHex(route.color);
                                    const isDark = isColorDark(colorHex);
                                    const isVeryLight = (hex: string): boolean => {
                                        if (!hex || hex.length < 7) return false;
                                        const r = parseInt(hex.slice(1, 3), 16);
                                        const g = parseInt(hex.slice(3, 5), 16);
                                        const b = parseInt(hex.slice(5, 7), 16);
                                        return r > 240 && g > 240 && b > 240; // Check if close to white
                                    };
                                    const needsBorder = isVeryLight(colorHex);
                                    const gradeText = route.grade !== undefined ? getGradeAsString(route.grade, GradeSystem.FRENCH) : 'N/A';

                                    return (
                                        <div key={route.id} className="ranking-list-item" onClick={() => handleRouteClick(route.id)}>
                                            <div className="ranking-list-item-grade">
                                                <div className="grade-circle" style={{ backgroundColor: colorHex, color: isDark ? 'white' : 'black', borderColor: needsBorder ? '#dddddd' : 'transparent' }}>
                                                    {gradeText}
                                                </div>
                                            </div>
                                            <div className="ranking-list-item-setter">
                                                <div className="setter-name">{truncateSetterName(route.setterName || 'Unknown setter')}</div>
                                            </div>
                                            <div className="ranking-list-item-rope">
                                                {route.rope || '-'}
                                            </div>
                                            <div className="ranking-list-item-bananas">
                                                {routeBananas.get(route.id) || 0}
                                            </div>
                                            <div className="ranking-list-item-performance">
                                                {(() => {
                                                    if (climbInfo.hasClimbed && climbInfo.performance) {
                                                        // Show specific performance type instead of generic "Topped"
                                                        if (climbInfo.performance === ClimbPerformance.ON_SIGHT) {
                                                            return <div className="performance-badge onsight">On Sight</div>;
                                                        }
                                                        if (climbInfo.performance === ClimbPerformance.FLASH) {
                                                            return <div className="performance-badge flash">Flash</div>;
                                                        }
                                                        if (climbInfo.performance === ClimbPerformance.REDPOINT) {
                                                            return <div className="performance-badge topped">Topped</div>;
                                                        }
                                                        if (climbInfo.performance === ClimbPerformance.TOPPED) {
                                                            return <div className="performance-badge topped">Topped</div>;
                                                        }
                                                        if ([ClimbPerformance.NOT_TOPPED, ClimbPerformance.BLOCKS].includes(climbInfo.performance) && climbInfo.hangs !== undefined) {
                                                            return <div className="performance-badge not-topped">{climbInfo.hangs} {climbInfo.hangs === 1 ? 'Hang' : 'Hangs'}</div>;
                                                        }
                                                        // Fallback for other performance types
                                                        return <div className={`performance-badge ${getPerformanceClass(climbInfo.performance)}`}>{getPerformanceLabel(climbInfo.performance, climbInfo.hangs)}</div>;
                                                    }
                                                    return '-';
                                                })()}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    )}
                    {/* --- End Routes List --- */}
                        </>
                    )}
                </div>
            )}

            {activeTab === 'climbs' && (
                <div className="climbs-section tab-content-area">
                    {userLoading ? (
                        <div className="tab-loading-indicator">Loading climbs...</div>
                    ) : (
                        <>
                            {/* --- Filter Bottom Drawer for Climbs --- */}
                            {showClimbsFilters && (
                                <div className="filter-drawer-overlay" onClick={() => setShowClimbsFilters(false)}>
                                    <div className="filter-drawer" onClick={(e) => e.stopPropagation()}>
                                        <div className="filter-drawer-header">
                                            <h3>Filter Climbs</h3>
                                            <button className="filter-drawer-close" onClick={() => setShowClimbsFilters(false)}>×</button>
                                        </div>
                                        <div className="filter-drawer-content">
                                            {Object.keys(climbsActiveFilters).length > 0 && (
                                                <div className="active-filters">
                                                    <span>Active:</span>
                                                    {climbsActiveFilters.grade && <span className="active-filter-tag">Grade: {climbsActiveFilters.grade}</span>}
                                                    {climbsActiveFilters.performance && <span className="active-filter-tag">Performance: {climbsActiveFilters.performance}</span>}
                                                    <button className="clear-filters-button" onClick={clearClimbsFilters}>Clear All</button>
                                                </div>
                                            )}

                                            <div className="filters-container">
                                                {climbsFilterOptions.grades.length > 0 && (
                                                    <div className="filter-group">
                                                        <h3>Grade</h3>
                                                        <div className="filter-options">
                                                            {climbsFilterOptions.grades.map(grade => (
                                                                <button key={`climb-grade-${grade}`} className={`filter-option ${climbsActiveFilters.grade === grade ? 'active' : ''}`} onClick={() => applyClimbsFilter('grade', grade)}>
                                                                    {grade}
                                                                </button>
                                                            ))}
                                                        </div>
                                                    </div>
                                                )}
                                                {climbsFilterOptions.performances.length > 0 && (
                                                    <div className="filter-group">
                                                        <h3>Performance</h3>
                                                        <div className="filter-options">
                                                            {climbsFilterOptions.performances.map(performance => (
                                                                <button key={`climb-performance-${performance}`} className={`filter-option ${climbsActiveFilters.performance === performance ? 'active' : ''}`} onClick={() => applyClimbsFilter('performance', performance)}>
                                                                    {performance}
                                                                </button>
                                                            ))}
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Active filters indicator */}
                            {Object.keys(climbsActiveFilters).length > 0 && (
                                <div className="active-filters-summary">
                                    <span>Filters applied: {Object.keys(climbsActiveFilters).length}</span>
                                    <button className="clear-filters-button" onClick={clearClimbsFilters}>Clear All</button>
                                </div>
                            )}

                    {/* Climbs List */}
                    {userClimbs.length === 0 ? (
                        <div className="empty-routes">
                            <p>No climbs recorded in this gym yet</p>
                        </div>
                    ) : filteredClimbs.length === 0 ? (
                        <div className="empty-routes">
                            <p>No climbs match your filters</p>
                            <button className="clear-filters-button" onClick={clearClimbsFilters}>Clear Filters</button>
                        </div>
                    ) : (
                        <div className="rankings-list-container">
                            <div className="rankings-list-scrollable">
                                <div className="rankings-list-header">
                                    <div 
                                        className={`ranking-list-header-grade ${sortBy === 'grade' ? 'sortable active' : 'sortable'}`} 
                                        onClick={() => handleSortChange('grade')}
                                    >
                                        Grade {sortBy === 'grade' && <span className="sort-direction">{sortDirection === 'asc' ? '↑' : '↓'}</span>}
                                    </div>
                                    <div 
                                        className={`ranking-list-header-setter ${sortBy === 'date' ? 'sortable active' : 'sortable'}`} 
                                        onClick={() => handleSortChange('date')}
                                    >
                                        Date {sortBy === 'date' && <span className="sort-direction">{sortDirection === 'asc' ? '↑' : '↓'}</span>}
                                    </div>
                                    <div className="ranking-list-header-rope">
                                        Rope
                                    </div>
                                    <div 
                                        className={`ranking-list-header-performance ${sortBy === 'performance' ? 'sortable active' : 'sortable'}`} 
                                        onClick={() => handleSortChange('performance')}
                                    >
                                        Performance {sortBy === 'performance' && <span className="sort-direction">{sortDirection === 'asc' ? '↑' : '↓'}</span>}
                                    </div>
                                </div>
                                {sortedClimbs.map((climb, index) => {
                                    const route = getRouteForClimb(climb.routeId, climb);
                                    const colorHex = route ? getColorHex(route.color) : '#cccccc';
                                    const isDark = isColorDark(colorHex);
                                    const isVeryLight = (hex: string): boolean => {
                                        if (!hex || hex.length < 7) return false;
                                        const r = parseInt(hex.slice(1, 3), 16);
                                        const g = parseInt(hex.slice(3, 5), 16);
                                        const b = parseInt(hex.slice(5, 7), 16);
                                        return r > 240 && g > 240 && b > 240;
                                    };
                                    const needsBorder = isVeryLight(colorHex);
                                    const gradeText = route && route.grade !== undefined ? getGradeAsString(route.grade, GradeSystem.FRENCH) : 'N/A';
                                    const backgroundColor = getClimbBackgroundColor(sortedClimbs, index);
                                    const originalIndex = findOriginalClimbIndex(climb);

                                    return (
                                        <div 
                                            key={`climb-${index}`} 
                                            className="ranking-list-item" 
                                            style={{ backgroundColor }}
                                            onClick={() => handleClimbClick(originalIndex)}
                                        >
                                            <div className="ranking-list-item-grade">
                                                <div className="grade-circle" style={{ backgroundColor: colorHex, color: isDark ? 'white' : 'black', borderColor: needsBorder ? '#dddddd' : 'transparent' }}>
                                                    {gradeText}
                                                </div>
                                            </div>
                                            <div className="ranking-list-item-setter">
                                                <div className="setter-name">
                                                    {new Date(climb.date).toLocaleDateString()}
                                                </div>
                                            </div>
                                            <div className="ranking-list-item-rope">
                                                {route?.rope || '-'}
                                                {route?.status === 'archived' && (
                                                    <div style={{ fontSize: '0.7em', color: '#666', marginTop: '2px' }}>
                                                        Archived
                                                    </div>
                                                )}
                                            </div>
                                            <div className="ranking-list-item-performance" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px' }}>
                                                <div className={`performance-badge ${getPerformanceClass(climb.performance)}`}>
                                                    {getPerformanceLabel(climb.performance, climb.hangs)}
                                                </div>
                                                <span style={{ 
                                                    color: climb.attempt && climb.attempt > 1 ? '#666' : 'transparent', 
                                                    fontSize: '0.9em', 
                                                    fontWeight: 'bold',
                                                    userSelect: climb.attempt && climb.attempt > 1 ? 'auto' : 'none'
                                                }}>
                                                    #{climb.attempt || 1}
                                                </span>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    )}
                        </>
                    )}
                </div>
            )}

            {activeTab === 'bananaCup' && (
                <div className="banana-cup-section tab-content-area">
                    {showNewsletter && currentUser ? (
                        <Newsletter 
                            onSkip={handleNewsletterSkip} 
                            userName={currentUser.displayName || 'Climber'} 
                            userId={currentUser.uid}
                            gymName={gym?.name} 
                        />
                    ) : (
                        /* **SAFE**: gymId is checked before initial fetch */
                        <BananaCupRankings gymId={gymId!} />
                    )}
                </div>
            )}

            {activeTab === 'feedback' && (
                <div className="feedback-section tab-content-area">
                    {feedbackLoading ? (
                        <div style={{ textAlign: 'center', padding: '20px' }}>
                            Loading feedback...
                        </div>
                    ) : feedbackError ? (
                        <div style={{ textAlign: 'center', padding: '20px', color: 'red' }}>
                            Error loading feedback: {feedbackError}
                        </div>
                    ) : gymFeedbacks.length === 0 ? (
                        <div style={{ textAlign: 'center', padding: '20px', color: '#666' }}>
                            No feedback yet for this gym.
                        </div>
                    ) : (
                        <div className="feedback-list">
                            {gymFeedbacks.map((feedback) => {
                                // Find the route name
                                const route = routes.find(r => r.id === feedback.routeId);
                                const routeName = route ? `${route.color} ${route.location ? `(${route.location})` : ''}` : 'Unknown Route';
                                
                                return (
                                    <div 
                                        key={feedback.id} 
                                        className="feedback-item" 
                                        onClick={() => setSelectedFeedback(feedback)}
                                        style={{
                                        border: '1px solid #e0e0e0',
                                        borderRadius: '8px',
                                        padding: '16px',
                                        marginBottom: '12px',
                                        backgroundColor: '#fff',
                                        cursor: 'pointer'
                                    }}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '8px' }}>
                                            <div>
                                                <div style={{ fontWeight: 'bold', marginBottom: '4px' }}>
                                                    Route: {routeName}
                                                    {route && route.grade !== undefined && (
                                                        <span style={{ color: '#666', marginLeft: '8px' }}>
                                                            {getGradeAsString(route.grade, GradeSystem.FRENCH)}
                                                        </span>
                                                    )}
                                                </div>
                                                <div style={{ color: '#666', fontSize: '0.9em' }}>
                                                    {new Date(feedback.createdAt).toLocaleDateString()}
                                                </div>
                                            </div>
                                            {feedback.stars && (
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                    <div style={{ color: '#f39c12' }}>
                                                        {'★'.repeat(feedback.stars)}{'☆'.repeat(5 - feedback.stars)}
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                        
                                        {feedback.comment && (
                                            <div style={{ marginBottom: '8px', lineHeight: '1.4' }}>
                                                {feedback.comment}
                                            </div>
                                        )}
                                        
                                        {feedback.suggestedGrade && (
                                            <div style={{ 
                                                display: 'inline-block',
                                                backgroundColor: '#f8f9fa',
                                                padding: '4px 8px',
                                                borderRadius: '4px',
                                                fontSize: '0.9em',
                                                color: '#666'
                                            }}>
                                                Suggested grade: {getGradeAsString(feedback.suggestedGrade, GradeSystem.FRENCH)}
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
            )}

            {/* Floating Action Buttons */}
            {/* Filter button - centered */}
            {(activeTab === 'routes' || activeTab === 'climbs' || activeTab === 'bananaCup') && (
                <div className="floating-action-button-center">
                    {activeTab === 'routes' && (
                        <MyIconButton 
                            onClick={toggleFilters} 
                            ariaLabel="Filter routes" 
                            className={`floating-action-button filter-fab ${Object.keys(activeFilters).length > 0 ? 'has-filters' : ''}`}
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3"></polygon>
                            </svg>
                            {Object.keys(activeFilters).length > 0 && (
                                <span className="filter-count">{Object.keys(activeFilters).length}</span>
                            )}
                        </MyIconButton>
                    )}

                    {activeTab === 'climbs' && (
                        <MyIconButton 
                            onClick={toggleClimbsFilters} 
                            ariaLabel="Filter climbs" 
                            className={`floating-action-button filter-fab ${Object.keys(climbsActiveFilters).length > 0 ? 'has-filters' : ''}`}
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3"></polygon>
                            </svg>
                            {Object.keys(climbsActiveFilters).length > 0 && (
                                <span className="filter-count">{Object.keys(climbsActiveFilters).length}</span>
                            )}
                        </MyIconButton>
                    )}

                    {activeTab === 'bananaCup' && (
                        <MyIconButton 
                            onClick={toggleBananaCupInfo} 
                            ariaLabel="Banana Cup Information" 
                            className="floating-action-button info-fab"
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <circle cx="12" cy="12" r="10"></circle>
                                <line x1="12" y1="16" x2="12" y2="12"></line>
                                <line x1="12" y1="8" x2="12.01" y2="8"></line>
                            </svg>
                        </MyIconButton>
                    )}
                </div>
            )}

            {/* Add route button - positioned to the right */}
            {canCreateRoutes() && activeTab === 'routes' && (
                <MyIconButton 
                    onClick={handleAddRoute} 
                    ariaLabel="Create climbing route" 
                    className="floating-action-button add-fab"
                >
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <line x1="12" y1="5" x2="12" y2="19"></line>
                        <line x1="5" y1="12" x2="19" y2="12"></line>
                    </svg>
                </MyIconButton>
            )}
            
            <ExitMessageOverlay 
                show={showExitMessage} 
                message={currentMessage} 
            />

            {/* Feedback Detail Modal */}
            {selectedFeedback && (
                <div className="modal-overlay" onClick={() => setSelectedFeedback(null)}>
                    <div className="modal-content" onClick={e => e.stopPropagation()} style={{ maxWidth: '500px', width: '90%' }}>
                        <div className="modal-header">
                            <h2 className="modal-title">Feedback Details</h2>
                            <MyIconButton 
                                onClick={() => setSelectedFeedback(null)} 
                                ariaLabel="Close dialog" 
                                className="close-button"
                            >
                                &times;
                            </MyIconButton>
                        </div>
                        <div className="modal-body" style={{ display: 'flex', flexDirection: 'column', gap: '16px', padding: '16px' }}>
                            {(() => {
                                const route = routes.find(r => r.id === selectedFeedback.routeId);
                                const routeName = route ? `${route.color} ${route.location ? `(${route.location})` : ''}` : 'Unknown Route';
                                
                                return (
                                    <>
                                        <div>
                                            <strong>Route:</strong> {routeName}
                                            {route && route.grade !== undefined && (
                                                <span style={{ color: '#666', marginLeft: '8px' }}>
                                                    {getGradeAsString(route.grade, GradeSystem.FRENCH)}
                                                </span>
                                            )}
                                        </div>
                                        <div>
                                            <strong>Date:</strong> {new Date(selectedFeedback.createdAt).toLocaleDateString()}
                                        </div>
                                        
                                        {selectedFeedback.stars && (
                                            <div>
                                                <strong>Rating:</strong>{' '}
                                                <span style={{ color: '#f39c12' }}>
                                                    {'★'.repeat(selectedFeedback.stars)}{'☆'.repeat(5 - selectedFeedback.stars)}
                                                </span>
                                            </div>
                                        )}
                                        
                                        {selectedFeedback.comment && (
                                            <div>
                                                <strong>Comment:</strong>
                                                <div style={{ marginTop: '4px', padding: '12px', backgroundColor: '#f8f9fa', borderRadius: '6px' }}>
                                                    {selectedFeedback.comment}
                                                </div>
                                            </div>
                                        )}
                                        
                                        {selectedFeedback.suggestedGrade !== undefined && (
                                            <div>
                                                <strong>Suggested Grade:</strong>{' '}
                                                <span style={{ backgroundColor: '#e9ecef', padding: '4px 8px', borderRadius: '4px' }}>
                                                    {getGradeAsString(selectedFeedback.suggestedGrade, GradeSystem.FRENCH)}
                                                </span>
                                            </div>
                                        )}
                                    </>
                                );
                            })()}
                        </div>
                        <div className="modal-footer" style={{ borderTop: '1px solid #eee', padding: '16px', display: 'flex', justifyContent: 'flex-end', gap: '12px' }}>
                            <button
                                onClick={() => setSelectedFeedback(null)}
                                style={{ padding: '8px 16px', border: '1px solid #ccc', borderRadius: '4px', background: 'transparent', cursor: 'pointer' }}
                            >
                                Close
                            </button>
                            <button
                                onClick={handleDeleteFeedback}
                                disabled={isDeletingFeedback}
                                style={{ padding: '8px 16px', backgroundColor: '#dc3545', color: 'white', border: 'none', borderRadius: '4px', cursor: isDeletingFeedback ? 'not-allowed' : 'pointer' }}
                            >
                                {isDeletingFeedback ? 'Deleting...' : 'Delete Feedback'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default GymDetail;
